import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/auth/AuthProvider";
import { Button } from "@/components/ui/button";
import HelpGenieLogo from "@/components/ui/HelpGenieLogo";
import { Roles } from '@/types/auth/auth';
import { Menu, X, ChevronDown, Car, Anchor, Tv, Printer, Home, Plane, Building2, Code2, Handshake, Key, Calendar, Smartphone, Target, BookOpen, MessageSquare, Settings, ClipboardList, Mic, Users } from "lucide-react";
import SolutionsDropdown from "./components/SolutionsDropdown";
import CapabilitiesDropdown from "./components/CapabilitiesDropdown";
import DiscoverDropdown from "./components/DiscoverDropdown";
import { getAllSolutionScenarios } from '@/data/solutions/registry';
import { getIndustryHref } from '@/config/site';
import { whoWeServeItems } from '@/data/marketing/whoWeServe';
import { capabilityItems } from '@/data/marketing/capabilities';

// Map solution IDs to their icons for mobile menu
const solutionIcons: Record<string, typeof Car> = {
  'automotive': Car,
  'marine': Anchor,
  'appliances': Tv,
  'office-equipment': Printer,
  'home-builders': Home,
  'travel-hospitality': Plane,
  'real-estate': Key,
  'clubs-associations': Users,
  'events': Calendar,
};

const whoWeServeIcons: Record<string, typeof Building2> = {
  business: Building2,
  developers: Code2,
  partners: Handshake,
  consumers: Smartphone,
};

export default function HomeNav() {
  const { user, userProfile, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSolutionsExpanded, setIsSolutionsExpanded] = useState(false);
  const [isCapabilitiesExpanded, setIsCapabilitiesExpanded] = useState(false);
  const [isDiscoverExpanded, setIsDiscoverExpanded] = useState(false);
  const scenarios = getAllSolutionScenarios();

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate("/signin");
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <header className="absolute top-0 left-0 right-0 z-30 w-full bg-transparent">
      <div className="w-full px-6 sm:px-10 lg:px-16 xl:px-20 py-4 sm:py-6">
        <div className="flex items-center justify-between">
          {/* Logo - Left */}
          <div className="flex-1">
            <a href="/" className="inline-flex items-center gap-2 sm:gap-3 cursor-pointer">
              <HelpGenieLogo size={32} className="sm:w-10 sm:h-10" />
              <div className="flex flex-col">
                <span className="font-bold text-lg sm:text-xl text-white tracking-tight">Help Genie</span>
                <span className="text-xs text-gray-300 -mt-1 hidden sm:block">Now We're Talking</span>
              </div>
            </a>
          </div>

          {/* Navigation Links - Center (always visible, signed in or out) */}
          <div className="hidden md:flex items-center justify-center gap-6">
            <SolutionsDropdown />
            <CapabilitiesDropdown />
            {/* Products menu item hidden for now
            <a
              href="/products"
              className="text-white font-medium hover:text-gray-200 transition px-2"
            >
              Products
            </a>
            */}
            <DiscoverDropdown />
            <a
              href="/pricing"
              className="text-white font-medium hover:text-gray-200 transition px-2"
            >
              Pricing
            </a>
          </div>

          {/* Auth Buttons - Right */}
          <div className="flex items-center justify-end gap-3 flex-1">
            <div className="hidden md:flex items-center gap-3">
              {!user ? (
                <>
                  <a
                    href="/signin"
                    className="rounded-full px-5 py-2 border-2 border-white text-white bg-white/10 font-semibold hover:bg-white/20 transition"
                  >
                    Sign In
                  </a>
                  <a href="/signup" className="rounded-full px-5 py-2 bg-white text-genie-primary font-semibold hover:bg-gray-200 transition">
                    Start Free
                  </a>
                </>
              ) : userProfile ? (
                <>
                  <Link
                    to={
                      userProfile.role === Roles.Admin
                        ? "/dashboard"
                        : "/genie-hub/my-genies"
                    }
                    className="rounded-full px-5 py-2 border-2 border-white text-white bg-white/10 font-semibold hover:bg-white/20 transition"
                  >
                    {userProfile.role === Roles.Admin ? "Dashboard" : "My Genies"}
                  </Link>
                  <Button
                    onClick={handleSignOut}
                    variant="outline"
                    className="rounded-full px-5 py-2 bg-white text-genie-primary font-semibold hover:bg-gray-200 transition border-transparent"
                  >
                    Sign Out
                  </Button>
                </>
              ) : (
                <div className="h-10 w-24 bg-gray-200/50 rounded-full animate-pulse"></div>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={toggleMobileMenu}
              className="md:hidden p-2 text-white hover:bg-white/10 rounded-lg transition"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden absolute top-full left-0 right-0 bg-black/95 backdrop-blur-lg border-t border-white/10">
          <div className="px-4 py-4 space-y-3">
            {/* Nav accordions - always visible, signed in or out */}
            {/* Solutions Accordion */}
                <div>
                  <button
                    onClick={() => setIsSolutionsExpanded(!isSolutionsExpanded)}
                    className="flex items-center justify-between w-full text-white font-medium py-2 hover:text-gray-200 transition"
                  >
                    <span>Solutions</span>
                    <ChevronDown 
                      className={`w-4 h-4 transition-transform duration-200 ${isSolutionsExpanded ? 'rotate-180' : ''}`} 
                    />
                  </button>
                  {isSolutionsExpanded && (
                    <div className="pl-4 pb-2 space-y-1">
                      {/* Who We Serve Section */}
                      <div className="py-2">
                        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Who We Serve</span>
                      </div>
                      {whoWeServeItems.map((segment) => {
                        const IconComponent = whoWeServeIcons[segment.id] || Building2;
                        return (
                          <a
                            key={segment.id}
                            href={segment.href}
                            className="flex items-center gap-3 py-2 text-gray-300 hover:text-white transition"
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            <div className="p-1.5 bg-gradient-to-br from-genie-primary/10 to-genie-secondary/10 rounded-lg">
                              <IconComponent className="w-4 h-4 text-genie-primary" />
                            </div>
                            <span className="text-sm">{segment.name}</span>
                          </a>
                        );
                      })}

                      {/* Solutions by Industry Section */}
                      <div className="py-2 mt-2 border-t border-white/10">
                        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Solutions by Industry</span>
                      </div>
                      {scenarios.map((scenario) => {
                        const IconComponent = solutionIcons[scenario.meta.id] || Car;
                        return (
                          <a
                            key={scenario.meta.id}
                            href={getIndustryHref(scenario.meta.slug)}
                            className="flex items-center gap-3 py-2 text-gray-300 hover:text-white transition"
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            <div className="p-1.5 bg-gradient-to-br from-genie-primary/10 to-genie-secondary/10 rounded-lg">
                              <IconComponent className="w-4 h-4 text-genie-primary" />
                            </div>
                            <span className="text-sm">{scenario.meta.name}</span>
                          </a>
                        );
                      })}
                    </div>
                  )}
                </div>
                {/* Products menu item hidden for now
                <a
                  href="/products"
                  className="block text-white font-medium py-2 hover:text-gray-200 transition"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Products
                </a>
                */}

                {/* Capabilities Accordion */}
                <div>
                  <button
                    onClick={() => setIsCapabilitiesExpanded(!isCapabilitiesExpanded)}
                    className="flex items-center justify-between w-full text-white font-medium py-2 hover:text-gray-200 transition"
                  >
                    <span>Capabilities</span>
                    <ChevronDown
                      className={`w-4 h-4 transition-transform duration-200 ${isCapabilitiesExpanded ? 'rotate-180' : ''}`}
                    />
                  </button>
                  {isCapabilitiesExpanded && (
                    <div className="pl-4 pb-2 space-y-1">
                      {/* Learn More Section */}
                      <div className="py-2">
                        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Learn More</span>
                      </div>
                      <a
                        href="/how-it-works"
                        className="flex items-center gap-3 py-2 text-gray-300 hover:text-white transition"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        <div className="p-1.5 bg-gradient-to-br from-genie-primary/10 to-genie-secondary/10 rounded-lg">
                          <Settings className="w-4 h-4 text-genie-primary" />
                        </div>
                        <span className="text-sm">How It Works</span>
                      </a>
                      <a
                        href="/playbooks"
                        className="flex items-center gap-3 py-2 text-gray-300 hover:text-white transition"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        <div className="p-1.5 bg-gradient-to-br from-genie-primary/10 to-genie-secondary/10 rounded-lg">
                          <ClipboardList className="w-4 h-4 text-genie-primary" />
                        </div>
                        <span className="text-sm">Playbooks</span>
                      </a>

                      {/* What You Can Do Section */}
                      <div className="py-2 mt-2 border-t border-white/10">
                        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">What You Can Do</span>
                      </div>
                      {capabilityItems.map((item) => {
                        const iconMap: Record<string, typeof Target> = {
                          'sales-lead-capture': Target,
                          'internal-information-delivery': BookOpen,
                          'product-support': MessageSquare,
                          'voice-experiences': Mic,
                        };
                        const IconComponent = iconMap[item.id] || Target;
                        return (
                          <a
                            key={item.id}
                            href={item.href}
                            className="flex items-center gap-3 py-2 text-gray-300 hover:text-white transition"
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            <div className="p-1.5 bg-gradient-to-br from-genie-primary/10 to-genie-secondary/10 rounded-lg">
                              <IconComponent className="w-4 h-4 text-genie-primary" />
                            </div>
                            <span className="text-sm">{item.name}</span>
                          </a>
                        );
                      })}
                    </div>
                  )}
                </div>
                {/* Discover Accordion */}
                <div>
                  <button
                    onClick={() => setIsDiscoverExpanded(!isDiscoverExpanded)}
                    className="flex items-center justify-between w-full text-white font-medium py-2 hover:text-gray-200 transition"
                  >
                    <span>Discover</span>
                    <ChevronDown
                      className={`w-4 h-4 transition-transform duration-200 ${isDiscoverExpanded ? 'rotate-180' : ''}`}
                    />
                  </button>
                  {isDiscoverExpanded && (
                    <div className="pl-4 pb-2 space-y-1">
                      {/* Explore Section */}
                      <div className="py-2">
                        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Explore</span>
                      </div>
                      <a
                        href="/discover"
                        className="flex items-center gap-3 py-2 text-gray-300 hover:text-white transition"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        <div className="p-1.5 bg-gradient-to-br from-genie-primary/10 to-genie-secondary/10 rounded-lg">
                          <img src="/icons/discover/icon-genie-examples.png" alt="Genie Examples" className="w-4 h-4 object-contain" />
                        </div>
                        <span className="text-sm">Genie Examples</span>
                      </a>
                      <a
                        href="/explore"
                        className="flex items-center gap-3 py-2 text-gray-300 hover:text-white transition"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        <div className="p-1.5 bg-gradient-to-br from-genie-primary/10 to-genie-secondary/10 rounded-lg">
                          <img src="/icons/discover/icon-genie-examples.png" alt="Experience" className="w-4 h-4 object-contain" />
                        </div>
                        <span className="text-sm">Experience</span>
                      </a>

                      {/* Resources Section */}
                      <div className="py-2 mt-2 border-t border-white/10">
                        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Resources</span>
                      </div>
                      {[
                        { label: 'Blog', href: '/blog', icon: '/icons/discover/icon-blog.png' },
                        { label: 'Guides', href: '/guides', icon: '/icons/discover/icon-guides.png' },
                        { label: 'Comparisons', href: '/compare', icon: '/icons/discover/icon-comparisons.png' },
                        { label: 'Use Cases', href: '/use-cases', icon: '/icons/discover/icon-use-cases.png' },
                        { label: 'Case Studies', href: '/case-studies', icon: '/icons/discover/icon-case-studies.png' },
                        { label: 'Insights', href: '/insights', icon: '/icons/discover/icon-insights.png' },
                        { label: 'Updates', href: '/updates', icon: '/icons/discover/icon-updates.png' },
                        { label: 'Integrations', href: '/integrations', icon: '/icons/discover/icon-integrations.png' },
                      ].map((item) => (
                        <a
                          key={item.href}
                          href={item.href}
                          className="flex items-center gap-3 py-2 text-gray-300 hover:text-white transition"
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          <div className="p-1.5 bg-gradient-to-br from-genie-primary/10 to-genie-secondary/10 rounded-lg">
                            <img src={item.icon} alt={item.label} className="w-4 h-4 object-contain" />
                          </div>
                          <span className="text-sm">{item.label}</span>
                        </a>
                      ))}
                    </div>
                  )}
                </div>
                <a
                  href="/pricing"
                  className="block text-white font-medium py-2 hover:text-gray-200 transition"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Pricing
                </a>

                {/* Auth section - varies by state; nav above is always visible */}
                {!user ? (
                  <div className="pt-2 mt-2 space-y-2 border-t border-white/10">
                    <a
                      href="/signin"
                      className="block text-center rounded-full px-5 py-2 border-2 border-white text-white bg-white/10 font-semibold hover:bg-white/20 transition"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      Sign In
                    </a>
                    <a
                      href="/signup"
                      className="block text-center rounded-full px-5 py-2 bg-white text-genie-primary font-semibold hover:bg-gray-200 transition"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      Start Free
                    </a>
                  </div>
                ) : userProfile ? (
                  <div className="pt-2 mt-2 space-y-2 border-t border-white/10">
                    <Link
                      to={
                        userProfile.role === Roles.Admin
                          ? "/dashboard"
                          : "/genie-hub/my-genies"
                      }
                      className="block text-center rounded-full px-5 py-2 border-2 border-white text-white bg-white/10 font-semibold hover:bg-white/20 transition"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      {userProfile.role === Roles.Admin ? "Dashboard" : "My Genies"}
                    </Link>
                    <Button
                      onClick={() => {
                        handleSignOut();
                        setIsMobileMenuOpen(false);
                      }}
                      variant="outline"
                      className="w-full rounded-full px-5 py-2 bg-white text-genie-primary font-semibold hover:bg-gray-200 transition border-transparent"
                    >
                      Sign Out
                    </Button>
                  </div>
                ) : null}
          </div>
        </div>
      )}
    </header>
  );
}
